import moment, { type Moment } from 'moment';
import React, { useEffect, useMemo } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';
import useTitle from 'hooks/useTitle';
import HolidayForm from './HolidayForm';
import * as routes from 'constants/routes';
import { useFetchHolidayByIdQuery } from 'hooks/query/useHolidayQueries';
import Loading from 'components/common/loading/Loading';
import BackButton from 'components/common/button/BackButton';

type HolidayRouteParams = {
  id?: string;
};

type HolidayRecord = {
  id: number;
  name: string;
  description: string;
  date: Moment;
  countries: string[] | null;
};

type HolidayByIdResponse = {
  data: Omit<HolidayRecord, 'date'> & { date: string };
};

const CreateEditHoliday: React.FC<RouteComponentProps<HolidayRouteParams>> = (props) => {
  const holidayId = props.match.params?.id;
  const title = holidayId ? 'Edit Holiday' : 'Add Holiday';
  const setTitle = useTitle();

  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  const {
    data: response,
    isLoading: loading,
    isError,
  } = useFetchHolidayByIdQuery<HolidayByIdResponse>(holidayId, Boolean(holidayId));

  useEffect(() => {
    if (isError) {
      history.push(routes.HOLIDAYS);
    }
  }, [isError]);

  const holiday = useMemo<HolidayRecord | null>(() => {
    if (!response?.data) {
      return null;
    }

    const { data } = response;

    return {
      ...data,
      countries: data.countries ?? null,
      date: moment(data.date),
    };
  }, [response]);

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div className="d-flex flex-row">
          <BackButton />
          <div className="d-flex flex-row">
            <div className="table-header">
              <h3 className="text-bold">{title}</h3>
            </div>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="loading-min-height">
          <Loading />
        </div>
      ) : (
        <HolidayForm holiday={holiday} />
      )}
    </div>
  );
};

export default CreateEditHoliday;
